// src/utils/discoverCategories.ts
//
// Categories aren't a real field on FeedItem — they're derived from
// the free-form `tags` array. This file defines the canonical
// buckets and the tag synonyms that map to each one. A card belongs
// to a category if any of its tags matches that category's synonyms
// (case-insensitive, hyphen/space normalized). Cards that match no
// category fall into "other".
//
// Adding a category later: pick a key, pick a label, list synonyms.
// Don't change existing keys (they're persisted in user preferences).

export interface CategoryDef {
  key: string;
  label: string;
  emoji: string;
  /** Lowercase, hyphen-normalized tag fragments. A tag matches if any
   *  fragment is a substring of the normalized tag. Substring (not
   *  whole-word) so that "art-gallery" matches "art" via the gallery
   *  fragment, "modern-art-museum" matches via "art-museum" or
   *  "museum", etc. */
  synonyms: string[];
}

export const DISCOVER_CATEGORIES: CategoryDef[] = [
  {
    key: 'food_drink',
    label: 'Food & Drink',
    emoji: '🍽️',
    synonyms: [
      'restaurant', 'cafe', 'café', 'coffee', 'bar', 'pub', 'bakery',
      'brewery', 'winery', 'food', 'dining', 'drinks', 'bistro',
      'eatery', 'tea', 'patisserie', 'gastropub', 'meal-takeaway',
      'meal-delivery', 'food-truck',
    ],
  },
  {
    key: 'culture_arts',
    label: 'Culture & Arts',
    emoji: '🎨',
    synonyms: [
      'museum', 'gallery', 'art', 'theater', 'theatre', 'library',
      'cultural', 'opera', 'performing-arts', 'exhibit', 'exhibition',
      'arts-center', 'concert-hall',
    ],
  },
  {
    key: 'outdoors',
    label: 'Outdoors',
    emoji: '🌳',
    synonyms: [
      'park', 'garden', 'viewpoint', 'trail', 'beach', 'nature',
      'mountain', 'scenic', 'hiking', 'lake', 'river', 'forest',
      'botanical', 'zoo', 'aquarium', 'natural-feature',
    ],
  },
  {
    key: 'history',
    label: 'History',
    emoji: '🏛️',
    synonyms: [
      'monument', 'historic', 'historical', 'heritage', 'ruins',
      'castle', 'fortress', 'archaeology', 'archaeological', 'memorial',
      'tomb', 'palace',
    ],
  },
  {
    key: 'religious',
    label: 'Religious',
    emoji: '⛪',
    synonyms: [
      'church', 'temple', 'mosque', 'synagogue', 'religious',
      'cathedral', 'basilica', 'shrine', 'chapel', 'monastery',
      'place-of-worship',
    ],
  },
  {
    key: 'shopping',
    label: 'Shopping',
    emoji: '🛍️',
    synonyms: [
      'market', 'shop', 'shopping', 'boutique', 'mall', 'store',
      'antiques', 'souvenir', 'bookstore', 'department-store',
      'clothing-store', 'jewelry-store', 'flea-market',
    ],
  },
  {
    key: 'entertainment',
    label: 'Entertainment',
    emoji: '🎭',
    synonyms: [
      'cinema', 'movie', 'club', 'nightclub', 'music-venue', 'concert',
      'nightlife', 'live-music', 'dance', 'casino', 'amusement',
      'bowling', 'arcade',
    ],
  },
  {
    key: 'architecture',
    label: 'Architecture',
    emoji: '🏰',
    synonyms: [
      'landmark', 'architectural', 'plaza', 'square', 'bridge', 'tower',
      'skyscraper', 'modern-architecture', 'building',
    ],
  },
  {
    key: 'other',
    label: 'Other',
    emoji: '📍',
    synonyms: [], // catch-all — nothing matches it directly
  },
];

/** Normalize a tag for matching: lowercase, replace spaces and
 *  underscores with hyphens. */
function normalizeTag(tag: string): string {
  return tag.toLowerCase().replace(/[_\s]+/g, '-');
}

/** Determine which categories a feed item belongs to based on its
 *  tags. An item can match multiple categories (a "historic café"
 *  is both Food & Drink and History). Always returns at least one
 *  category — falls back to "other" if nothing matched.
 *
 *  Result is a Set for cheap intersection with the user's filter. */
export function categoriesForTags(tags: string[] | undefined): Set<string> {
  const out = new Set<string>();
  if (!tags || tags.length === 0) {
    out.add('other');
    return out;
  }
  const normalized = tags.map(normalizeTag);
  for (const cat of DISCOVER_CATEGORIES) {
    if (cat.key === 'other') continue;
    for (const syn of cat.synonyms) {
      if (normalized.some((t) => t.includes(syn))) {
        out.add(cat.key);
        break;
      }
    }
  }
  if (out.size === 0) out.add('other');
  return out;
}

/** All category keys as a Set — used for the "everything enabled"
 *  default state. */
export const ALL_CATEGORY_KEYS: Set<string> = new Set(
  DISCOVER_CATEGORIES.map((c) => c.key)
);
